import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { makeStyles } from '@material-ui/core'
import RoundedModal from '../../../../molecules/RoundedModal'
import SydButton from '../../../../commonDesign/Button'
import SydLabel, { hookFormFieldErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import { useCreateOwnerGroupMutation } from '../../../../../api/householdRegistrations'

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '0 4px'
  },
  conflict: {
    background: 'var(--color-surface-warning-subtle, #fff8e1)',
    border: '1px solid var(--color-border-warning, #ffe082)',
    borderRadius: '4px',
    padding: '0.75rem',
    fontSize: '0.875rem',
    lineHeight: 1.4
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '8px'
  }
}))

/**
 * Modal for creating a new owner group (levels.groups, levelTypeId=207).
 *
 * onCreated(group): called after a successful create. group = { groupId, longName }.
 *   The parent uses this to optionally chain into an assignment afterwards.
 *
 * 409 path: when a group with the same name already exists for this firm
 * at levelTypeId=207, the server returns the existing groupId in the error
 * payload. We surface a "Use existing" affordance so the admin can adopt it
 * instead of creating a duplicate.
 */
function CreateOwnerGroupModal ({ open, onClose, clientId, onCreated }) {
  const classes = useStyles()
  const { mutateAsync: createMut } = useCreateOwnerGroupMutation()

  const { handleSubmit, control, formState, reset } = useForm({
    mode: 'onChange',
    defaultValues: { name: '' }
  })

  const [processing, setProcessing] = useState(false)
  const [conflict, setConflict] = useState(null)

  const closeAndReset = useCallback(() => {
    reset({ name: '' })
    setConflict(null)
    setProcessing(false)
    onClose()
  }, [reset, onClose])

  const onSubmit = useCallback(async (form) => {
    setProcessing(true)
    setConflict(null)
    try {
      const result = await createMut({ clientId, name: form.name.trim() })
      onCreated?.({ groupId: result.groupId, longName: result.longName })
      closeAndReset()
    } catch (err) {
      const status = err?.response?.data?.statusCode || err?.response?.status
      const data = err?.response?.data?.data
      if (status === 409 && data?.groupId) {
        setConflict({ groupId: data.groupId, longName: data.longName, shortName: data.shortName })
      } else {
        alert('There was a problem creating the owner group')
        console.error(err)
      }
    } finally {
      setProcessing(false)
    }
  }, [createMut, clientId, onCreated, closeAndReset])

  const adoptExisting = useCallback(() => {
    if (!conflict) return
    onCreated?.({ groupId: conflict.groupId, longName: conflict.longName })
    closeAndReset()
  }, [conflict, onCreated, closeAndReset])

  return (
    <RoundedModal
      title='Create Owner Group'
      open={open}
      onClose={closeAndReset}
      size='small'
      position='top'
      disablePortal={false}
    >
      <div className={classes.form}>
        <Controller
          control={control}
          name='name'
          rules={{
            required: { value: true, message: 'Owner group name is required' },
            maxLength: { value: 96, message: 'Owner group name must be 96 characters or less' }
          }}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Owner Group Name'
              description='Used for ownership rollups in reporting (max 96 characters).'
              error={hookFormFieldErrorAdapter(fieldState)}
            >
              <SydInput {...field} disabled={processing} />
            </SydLabel>
          )}
        />

        {conflict ? (
          <div className={classes.conflict}>
            An owner group named <strong>{conflict.longName}</strong> already exists for this firm. You can adopt it instead of creating a duplicate.
          </div>
        ) : null}

        <div className={classes.actions}>
          <SydButton variant='ghost' onClick={closeAndReset} disabled={processing}>Cancel</SydButton>
          {conflict ? (
            <SydButton variant='primary' onClick={adoptExisting}>Use existing</SydButton>
          ) : (
            <SydButton
              variant='primary'
              onClick={handleSubmit(onSubmit)}
              disabled={!formState.isValid || processing}
              processing={processing}
            >
              Create
            </SydButton>
          )}
        </div>
      </div>
    </RoundedModal>
  )
}

CreateOwnerGroupModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientId: PropTypes.number.isRequired,
  onCreated: PropTypes.func
}

export default CreateOwnerGroupModal
